import { Dropdown, Separator } from '@heroui/react';
import { BookOpen, Check, ChevronDown, LayoutDashboard, LogOut, ShieldCheck, UserRound } from 'lucide-react';
import { ThemeControl } from './ThemeControl';
import './UserMenu.css';

export type AppView = 'board' | 'admin';

interface UserMenuProps {
  email?: string;
  view?: AppView;
  onView?: (view: AppView) => void;
  onRules?: () => void;
  onLogout?: () => void;
}

function initials(email?: string) {
  const parts =
    email
      ?.split('@')[0]
      ?.split(/[._\-+]+/)
      .filter(Boolean) ?? [];
  return parts
    .slice(0, 2)
    .map((part) => Array.from(part)[0])
    .join('')
    .toLocaleUpperCase('es');
}

export function UserMenu({ email, view, onView, onRules, onLogout }: UserMenuProps) {
  const monogram = initials(email);
  return (
    <Dropdown>
      <Dropdown.Trigger className="user-menu-trigger" aria-label="Menú de usuario">
        <span className="user-menu-avatar" aria-hidden="true">
          {monogram || <UserRound size={17} strokeWidth={1.5} />}
        </span>
        <span className="user-menu-trigger-label">Mi cuenta</span>
        <ChevronDown className="user-menu-chevron" size={13} aria-hidden="true" />
      </Dropdown.Trigger>
      <Dropdown.Popover className="user-menu-popover" placement="bottom end" offset={12}>
        <div className="user-menu-account">
          <span className="user-menu-avatar user-menu-avatar--large" aria-hidden="true">
            {monogram || <UserRound size={20} strokeWidth={1.5} />}
          </span>
          <div className="user-menu-identity">
            <p className="user-menu-account-label">Tu cuenta de Eleva</p>
            <p className="user-menu-email">{email ?? 'Sesión iniciada'}</p>
            {onView && <span className="user-menu-account-role">Administrador</span>}
          </div>
        </div>
        <Dropdown.Menu className="user-menu-list">
          {onView && (
            <>
              <Dropdown.Item
                id="board"
                className="user-menu-item"
                textValue="Reunión operativa"
                data-current={view === 'board' ? 'true' : undefined}
                onAction={() => onView('board')}
              >
                <LayoutDashboard size={17} strokeWidth={1.5} aria-hidden="true" />
                <span>Reunión operativa</span>
                {view === 'board' && <span className="sr-only">Vista actual</span>}
                {view === 'board' && <Check className="user-menu-current" size={15} aria-hidden="true" />}
              </Dropdown.Item>
              <Dropdown.Item
                id="admin"
                className="user-menu-item"
                textValue="Administración"
                data-current={view === 'admin' ? 'true' : undefined}
                onAction={() => onView('admin')}
              >
                <ShieldCheck size={17} strokeWidth={1.5} aria-hidden="true" />
                <span>Administración</span>
                {view === 'admin' && <span className="sr-only">Vista actual</span>}
                {view === 'admin' && <Check className="user-menu-current" size={15} aria-hidden="true" />}
              </Dropdown.Item>
            </>
          )}
          {onView && (onRules || onLogout) && <Separator className="user-menu-separator" />}
          {onRules && (
            <Dropdown.Item
              id="rules"
              className="user-menu-item"
              textValue="Cómo se calcula"
              onAction={onRules}
            >
              <BookOpen size={17} strokeWidth={1.5} aria-hidden="true" />
              <span>Cómo se calcula</span>
            </Dropdown.Item>
          )}
          {onRules && onLogout && <Separator className="user-menu-separator" />}
          {onLogout && (
            <Dropdown.Item
              id="logout"
              className="user-menu-item user-menu-signout"
              textValue="Cerrar sesión"
              onAction={onLogout}
            >
              <LogOut size={17} strokeWidth={1.5} aria-hidden="true" />
              <span>Cerrar sesión</span>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
        <ThemeControl />
      </Dropdown.Popover>
    </Dropdown>
  );
}
